import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SHAPE_GEOMETRY } from '../../lib/shapes';
import { fillToCss } from '../../lib/colors';
import { GradientSvgDefs } from '../../lib/gradients';
import { sanitizeHtml, textToHtml, trackingToLetterSpacing, scaleInlineHtml, markSpecialChars } from '../../lib/textUtils';
import { hasCrop } from '../../lib/pictureCrop';
import { isCropShape, cropShapeClip, cropShapePath, cropShapeNeedsDefs, cropShapeEvenOdd } from '../../lib/cropShape';
import { linkedDisplay, caretPlainOffset, placeCaretAtPlainOffset, locateSplit, appendStoryFragment } from '../../lib/storyEdit';
import { editingStore, saveSelection, settleTypingSpans, pruneTypingSpans, dropTypingSpanBeforeBackspace, stripTypingArtifacts, absorbRootText, placeCaretAtPoint, ZWSP, normalizeScaleSpans, SCALE_CLASS, showRememberedSelection, hideRememberedSelection } from '../../lib/editingStore';
import { DEFAULT_TEXT_PROPS } from '../../context/EditorContext';
import { scaleLineHeight } from '../../lib/units';
import { layoutTextBox, isEngineEligible, needsEngine, isVerticalText, DEFAULT_TAB_STOP, baselineGrid } from '../../lib/textLayout';
import { hasShapeText, shapeTextObject } from '../../lib/shapeText';
import { computeWrapObstacles, editExclusions, padsOf } from '../../lib/textWrap';
import { buildFrameEffects, hasFrameEffects, textEffectShadows, textReflectionCss } from '../../lib/effects';
import { dashArray, cssDash, strokeHasGradient, strokeEndColors, tableBorderStroke } from '../../lib/fills';
import { ligaturesCss, fontFeatureSettings, hasWordArtTransform } from '../../lib/wordart';
import { WordArtSvg } from './WordArtText';
import { OutlineSvg, ShimmerDefs, TableBordersSvg } from './OutlineSvg';
import { freeformToD } from '../../lib/freeform';
import { TAB_SELECTOR, createTabSpan, materializeTabs, dematerializeTabs, layoutTabs, htmlHasTabs, tabOptionsOf, tabOptionsKey } from '../../lib/tabLayout';
import { useFontsVersion } from '../../hooks/useFontsVersion';

/** Browsers floor fractional CSS border widths to whole device pixels; round instead so zoomed strokes keep their weight. */
const cssBorderPx = (w, scale) => Math.max(1, Math.round(w * scale));

/**
 * Geometry descriptor for an edited freeform (Edit Points). Like Office, an opened path keeps its fill (the fill area
 * closes implicitly) and only the stroke shows the gap; freeforms converted from lines / connectors have no fill
 * until one of their paths is closed (`noFill`).
 */
const freeformGeometry = (ff) => ({ kind: 'freeform', d: freeformToD(ff).all, open: !!ff.noFill, evenodd: !!ff.evenodd });

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

export const ShapeSvg = ({ shapeType, props = {}, id, width, height, scale = 1 }) => {
  const freeform = shapeType === 'freeform' && props.freeform?.paths ? props.freeform : null;
  const geom = freeform ? freeformGeometry(freeform) : (SHAPE_GEOMETRY[shapeType] || SHAPE_GEOMETRY.rectangle);
  const { fill, stroke = {}, opacity = 100, cornerRadius = 0, flipX, flipY } = props;
  const strokeWidth = stroke?.width || 0;
  const strokeColor = stroke?.color || '#000000';
  const dash = dashArray(stroke?.dash);
  const defsId = `wp-${id}`;
  const isLine = geom.kind === 'line' || geom.open;
  const transform = `${flipX ? 'scaleX(-1)' : ''} ${flipY ? 'scaleY(-1)' : ''}`.trim();
  // Gradient outline (stroke.gradient): CSS boxes get a transparent border + OutlineSvg overlay, paths a paint server.
  const gradientOutline = strokeHasGradient(stroke) && (strokeWidth > 0 || isLine);
  const W = Math.max(1, width || 100), H = Math.max(1, height || 100);

  // rectangles/rounded rects/ellipses are rendered with CSS for perfect gradient/image fills
  // (shadows and other effects are applied by EffectsFrame around the whole object)
  if (geom.kind === 'rect' || geom.kind === 'ellipse') {
    const radiusPx = (geom.kind === 'ellipse' ? 0 : geom.rounded ? Math.max(cornerRadius, Math.min(width, height) * 0.15) : cornerRadius) * scale;
    const bw = strokeWidth ? cssBorderPx(strokeWidth, scale) : 0;
    const style = {
      width: '100%', height: '100%',
      background: fillToCss(fill, '#cccccc'),
      borderRadius: geom.kind === 'ellipse' ? '50%' : radiusPx,
      border: bw ? `${bw}px ${gradientOutline ? 'solid transparent' : `${cssDash(stroke?.dash)} ${strokeColor}`}` : 'none',
      opacity: opacity / 100,
      boxSizing: 'border-box',
      transform: transform || undefined,
    };
    if (!gradientOutline) return <div style={style} data-shape={shapeType} />;
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', opacity: opacity / 100, transform: transform || undefined }} data-shape={shapeType} data-gradient-outline="1">
        <div style={{ ...style, opacity: 1, transform: undefined }} />
        <OutlineSvg id={`${defsId}-ol`} stroke={stroke} width={W * scale} height={H * scale} strokeWidth={bw} radius={radiusPx} ellipse={geom.kind === 'ellipse'} />
      </div>
    );
  }

  const svgFill = () => {
    if (isLine && !geom.closed) return 'none';
    if (!fill || fill.type === 'none') return 'transparent';
    if (fill.type === 'gradient') return `url(#${defsId}-grad)`;
    if (fill.type === 'image' && fill.image?.url) return `url(#${defsId}-img)`;
    return fill.color || '#cccccc';
  };

  const defs = [];
  if (fill?.type === 'gradient' && fill.gradient) {
    // The 100×100 viewBox is stretched to the object's size: compute the gradient in pixel space (like CSS does for the
    // rectangles above) and map it into user units, so angles, circles and multi-stop runs match the CSS rendering.
    defs.push(<GradientSvgDefs key="g" id={`${defsId}-grad`} gradient={fill.gradient} width={W} height={H} sx={100 / W} sy={100 / H} />);
  }
  if (gradientOutline) defs.push(<GradientSvgDefs key="s" id={`${defsId}-stroke`} gradient={stroke.gradient} width={W} height={H} sx={100 / W} sy={100 / H} />);
  const strokePaint = gradientOutline ? `url(#${defsId}-stroke)` : strokeColor;
  // Shimmer: a highlight-painted, stroke-only copy of the geometry sweeps on top (screen only, see .wp-shimmer).
  const shimmer = gradientOutline && !!stroke.shimmer;
  if (shimmer) defs.push(<ShimmerDefs key="sh" id={`${defsId}-shimmer`} width={100} height={100} />);
  const shimmerPaint = `url(#${defsId}-shimmer)`;
  if (fill?.type === 'image' && fill.image?.url) {
    if (fill.image.fit === 'tile') {
      // Tiled texture: the outer svg stretches its 100x100 viewBox to the object's size, so express a 160px square tile
      // in user units per axis to keep the texture undistorted.
      const tw = (160 * 100) / Math.max(1, width || 100);
      const th = (160 * 100) / Math.max(1, height || 100);
      defs.push(
        <pattern key="p" id={`${defsId}-img`} patternUnits="userSpaceOnUse" width={tw} height={th}>
          <image href={fill.image.url} width={tw} height={th} preserveAspectRatio="none" />
        </pattern>
      );
    } else {
      const par = fill.image.fit === 'contain' ? 'xMidYMid meet' : fill.image.fit === 'stretch' ? 'none' : 'xMidYMid slice';
      defs.push(
        <pattern key="p" id={`${defsId}-img`} patternUnits="objectBoundingBox" width="1" height="1" viewBox="0 0 100 100" preserveAspectRatio={par}>
          <image href={fill.image.url} width="100" height="100" preserveAspectRatio={par} />
        </pattern>
      );
    }
  }
  const arrowColors = strokeEndColors(stroke);
  if (geom.arrowEnd || props.arrowEnd) defs.push(<marker key="me" id={`${defsId}-ae`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 Z" fill={arrowColors.end} /></marker>);
  if (geom.arrowStart || props.arrowStart) defs.push(<marker key="ms" id={`${defsId}-as`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 Z" fill={arrowColors.start} /></marker>);

  const common = {
    fill: svgFill(),
    stroke: strokeWidth || isLine ? strokePaint : 'none',
    strokeWidth: (strokeWidth || (isLine ? 2 : 0)) * scale,
    strokeDasharray: dash,
    vectorEffect: 'non-scaling-stroke',
    strokeLinejoin: 'round',
    strokeLinecap: 'round',
    fillRule: geom.evenodd ? 'evenodd' : undefined,
    markerEnd: geom.arrowEnd || props.arrowEnd ? `url(#${defsId}-ae)` : undefined,
    markerStart: geom.arrowStart || props.arrowStart ? `url(#${defsId}-as)` : undefined,
  };

  let element = null;
  let shimmerPass = null; // stroke-only copy for the shimmer layer (no fill, no arrowheads)
  const passProps = { ...common, fill: 'none', stroke: shimmerPaint, markerEnd: undefined, markerStart: undefined };
  if (geom.kind === 'line') {
    const lw = (strokeWidth || 2) * scale;
    element = <line x1="0" y1="50" x2="100" y2="50" {...common} stroke={strokePaint} strokeWidth={Math.max(lw, 0.75)} fill="none" />;
    if (shimmer) shimmerPass = <line x1="0" y1="50" x2="100" y2="50" {...passProps} strokeWidth={Math.max(lw, 0.75)} />;
  } else if (geom.kind === 'polygon') {
    element = <polygon points={geom.points} {...common} />;
    if (shimmer) shimmerPass = <polygon points={geom.points} {...passProps} />;
  } else if (geom.kind === 'freeform') {
    // Edited freeform: fill pass (no stroke) + stroke pass (no fill) so open sub-paths keep their gap.
    element = (
      <>
        {!geom.open && <path d={geom.d} {...common} stroke="none" markerEnd={undefined} markerStart={undefined} />}
        <path d={geom.d} {...common} fill="none" stroke={strokeWidth || geom.open ? strokePaint : 'none'} strokeWidth={(strokeWidth || (geom.open ? 2 : 0)) * scale} />
      </>
    );
    if (shimmer) shimmerPass = <path d={geom.d} {...passProps} strokeWidth={(strokeWidth || (geom.open ? 2 : 0)) * scale} />;
  } else {
    element = <path d={geom.d} {...common} fill={geom.open ? 'none' : common.fill} />;
    if (shimmer) shimmerPass = <path d={geom.d} {...passProps} />;
  }

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%" style={{ display: 'block', opacity: opacity / 100, overflow: 'visible', transform: transform || undefined }} data-shape={shapeType} data-shimmer={shimmer ? '1' : undefined}>
      {defs.length > 0 && <defs>{defs}</defs>}
      {element}
      {shimmerPass && <g className="wp-shimmer">{shimmerPass}</g>}
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* Text                                                                */
/* ------------------------------------------------------------------ */

export const buildTextStyles = (rawProps, scale = 1) => {
  const p = { ...DEFAULT_TEXT_PROPS, ...rawProps };
  const fontSize = (p.fontSize || 16) * scale;
  const ls = (p.letterSpacing || 0) * scale + trackingToLetterSpacing(p.tracking || 100, fontSize);
  const effects = p.textEffects || {};
  const shadows = textEffectShadows(effects, scale);

  const textFillCss = p.textFill && p.textFill.type && p.textFill.type !== 'solid' ? fillToCss(p.textFill) : null;
  const color = p.textFill?.type === 'solid' && p.textFill.color ? p.textFill.color : p.color;

  const container = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    paddingTop: (p.paddingTop ?? p.padding ?? 4) * scale,
    paddingRight: (p.paddingRight ?? p.padding ?? 4) * scale,
    paddingBottom: (p.paddingBottom ?? p.padding ?? 4) * scale,
    paddingLeft: (p.paddingLeft ?? p.padding ?? 4) * scale,
    background: p.background && p.background.type !== 'none' ? fillToCss(p.background) : 'transparent',
    // Gradient outline: the border stays (transparent) so padding / clipping do not move; TextContent draws the gradient in an OutlineSvg overlay.
    border: p.stroke?.width ? `${cssBorderPx(p.stroke.width, scale)}px ${strokeHasGradient(p.stroke) ? 'solid transparent' : `${cssDash(p.stroke.dash)} ${p.stroke.color || '#000'}`}` : 'none',
    borderRadius: (p.cornerRadius || 0) * scale,
    opacity: (p.opacity ?? 100) / 100,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: p.verticalAlign === 'middle' ? 'center' : p.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
    overflow: 'hidden',
    position: 'relative',
  };

  const text = {
    fontFamily: `'${p.fontFamily || 'Inter'}', sans-serif`,
    fontSize,
    fontWeight: p.fontWeight === 'bold' || p.fontWeight === '700' ? 700 : p.fontWeight || 400,
    fontStyle: p.fontStyle || 'normal',
    textDecoration: p.textDecoration && p.textDecoration !== 'none' ? p.textDecoration : 'none',
    color: textFillCss ? 'transparent' : color,
    background: textFillCss || undefined, // clipped to the glyphs by the .wp-text-fill class (index.css) — see TextContent
    textAlign: p.textAlign || 'left',
    lineHeight: scaleLineHeight(p.lineHeight || 1.2, scale), // a multiple, or an exact height ("16px" = Exactly 12 pt) that scales like the font
    letterSpacing: ls ? `${ls}px` : 'normal',
    textTransform: p.textTransform && p.textTransform !== 'none' ? p.textTransform : 'none',
    fontVariant: p.fontVariant === 'small-caps' ? 'small-caps' : 'normal',
    fontVariantPosition: p.fontVariantPosition === 'sub' ? 'sub' : p.fontVariantPosition === 'super' ? 'super' : 'normal',
    fontVariantLigatures: ligaturesCss(p.ligatures),
    fontFeatureSettings: fontFeatureSettings(p),
    fontVariantNumeric: p.numberStyle && p.numberStyle !== 'default' ? { lining: 'lining-nums', oldstyle: 'oldstyle-nums', tabular: 'tabular-nums', proportional: 'proportional-nums' }[p.numberStyle] : 'normal',
    WebkitTextStroke: p.textOutline?.width ? `${p.textOutline.width * scale}px ${p.textOutline.color || '#000'}` : undefined,
    // With gradient/picture text fills the glyphs are transparent (background-clip: text), so text-shadow would show
    // through them; use filter: drop-shadow() on the rendered pixels instead, as WordArt does.
    textShadow: shadows.length && !textFillCss ? shadows.join(', ') : undefined,
    filter: shadows.length && textFillCss ? shadows.map((sh) => `drop-shadow(${sh})`).join(' ') : undefined,
    hyphens: p.hyphenation ? 'auto' : 'manual',
    writingMode: p.textDirection === 'vertical' ? 'vertical-rl' : 'horizontal-tb',
    columnCount: p.columns > 1 ? p.columns : undefined,
    columnGap: p.columns > 1 ? (p.columnGap || 16) * scale : undefined,
    columnRule: p.columns > 1 && p.columnRule ? `1px solid ${p.columnRuleColor || 'rgba(0,0,0,0.2)'}` : undefined,
    textIndent: p.firstLineIndent ? `${p.firstLineIndent * scale}px` : undefined,
    paddingLeft: p.indentLeft ? p.indentLeft * scale : undefined,
    paddingRight: p.indentRight ? p.indentRight * scale : undefined,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    width: p.textScale && p.textScale !== 100 ? `${10000 / p.textScale}%` : '100%',
    transform: p.textScale && p.textScale !== 100 ? `scaleX(${p.textScale / 100})` : undefined,
    transformOrigin: 'left top',
    WebkitBoxReflect: textReflectionCss(effects.reflection, scale),
    '--wp-sb': `${(p.spaceBefore || 0) * scale}px`,
    '--wp-sa': `${(p.spaceAfter || 0) * scale}px`,
    '--wp-dc-lines': p.dropCap?.lines || 3,
    outline: 'none',
    minHeight: '1em',
    flexShrink: 0,
  };
  return { container, text, props: p };
};

const plainToHtml = (text, listStyle) => {
  const lines = String(text ?? '').split('\n');
  if (listStyle === 'bullet') return `<ul>${lines.map((l) => `<li>${escape(l)}</li>`).join('')}</ul>`;
  if (listStyle === 'number') return `<ol>${lines.map((l) => `<li>${escape(l)}</li>`).join('')}</ol>`;
  return textToHtml(text ?? '');
};
const escape = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const getDisplayHtml = (props, textOverride) => {
  if (textOverride !== undefined && textOverride !== null) return plainToHtml(textOverride, props.listStyle);
  if (props.html) return sanitizeHtml(props.html);
  return plainToHtml(props.text ?? '', props.listStyle);
};

/**
 * Remove wrap-exclusion helper nodes, turn tab spans back into tab characters and drop typing-style placeholders
 * (zero-width spaces) before persisting contentEditable HTML.
 */
export const cleanEditableHtml = (el) => {
  if (!el) return '';
  if (!el.querySelector(`[data-wp-exclusion], [data-wp-typing], .${SCALE_CLASS}, ${TAB_SELECTOR}`) && !el.textContent.includes(ZWSP)) return el.innerHTML;
  const c = el.cloneNode(true);
  c.querySelectorAll('[data-wp-exclusion]').forEach((n) => n.remove());
  dematerializeTabs(c);
  stripTypingArtifacts(c);
  normalizeScaleSpans(c, { final: true }); // a space still waiting inside a scaled word moves out before the HTML is kept
  return c.innerHTML;
};

/**
 * Display HTML laid out by the browser, with tab characters honoured as real tab stops (see lib/tabLayout.js).
 * `root` = the text element that carries indents / zoom (defaults to the parent); re-laid out when fonts arrive.
 */
const TabbedHtml = ({ html, tabs, rootRef }) => {
  const ref = useRef(null);
  const lastHtml = useRef(null);
  const fontsVersion = useFontsVersion();
  const key = tabOptionsKey(tabs);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (lastHtml.current !== html) {
      el.innerHTML = html;
      lastHtml.current = html;
      if (htmlHasTabs(html)) materializeTabs(el);
    }
    if (el.querySelector(TAB_SELECTOR)) layoutTabs(rootRef?.current || el.parentElement, tabs);
    // `tabs` is a fresh array on every render — `key` is its stable serialisation; rootRef is a ref object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, key, fontsVersion]);
  return <div ref={ref} />;
};

/**
 * Lays its content out in box space (unscaled px: the font sizes, indents and width the measurement uses) and shows it
 * at `scale` with a transform. CSS `zoom` and scaled font sizes both re-wrap the text at every zoom level (glyph
 * advances do not scale linearly), so the last line of a box could show at 100 % and be clipped at 50 % or 200 % — and
 * a linked story would appear to lose words between its boxes. A transform keeps the wrapping of the measurement at
 * any zoom. The wrapper takes the scaled height in the flex flow (measured with a ResizeObserver) so vertical
 * alignment (middle / bottom) still works.
 */
const ScaledBlock = ({ scale = 1, width, measureKey = '', className, style, innerRef, outerRef, children, ...rest }) => {
  const ref = useRef(null);
  const [h, setH] = useState(0);
  const measure = () => { const el = ref.current; if (!el) return; const nh = el.offsetHeight; setH((prev) => (Math.abs(prev - nh) > 0.5 ? nh : prev)); };
  const measureRef = useRef(measure);
  measureRef.current = measure;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureRef.current()); // typing, fonts arriving, late layout
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // content / zoom changes are measured synchronously (before paint) so vertical alignment never flashes
  useLayoutEffect(() => { measureRef.current(); }, [measureKey, scale, width]);
  return (
    <div ref={outerRef} style={{ position: 'relative', width: '100%', height: h * scale, flexShrink: 0 }} data-scaled-block="1">
      <div ref={(el) => { ref.current = el; if (innerRef) innerRef(el); }} className={className} style={{ ...style, position: 'absolute', left: 0, top: 0, width, transform: scale === 1 && !style?.transform ? undefined : `scale(${scale}) ${style?.transform || ''}`.trim(), transformOrigin: 'left top' }} {...rest}>
        {children}
      </div>
    </div>
  );
};

const decorationOf = (s) => [s.underline && 'underline', s.strike && 'line-through'].filter(Boolean).join(' ') || 'none';

/** Absolutely positioned lines produced by the layout engine (text flowing around obstacles). */
export const LayoutLines = ({ layout, scale = 1, text, classes }) => {
  const gradientText = text.background && text.color === 'transparent';
  return (
    <div className={classes} style={{ position: 'relative', flex: 1, width: '100%', minHeight: 0, outline: 'none', lineHeight: 'normal' }} data-testid="textbox-wrapped" aria-label="wrapped text">
      {layout.lines.map((ln, li) => ln.chunks.map((c, ci) => {
        const s = c.style || {};
        return (
          <span
            key={`${li}-${ci}`}
            className={gradientText ? 'wp-text-fill' : undefined}
            style={{
              position: 'absolute',
              left: c.x * scale,
              top: ln.y * scale,
              height: ln.h * scale,
              lineHeight: `${ln.h * scale}px`,
              whiteSpace: 'pre',
              fontFamily: `'${s.fontFamily || 'Inter'}', sans-serif`,
              fontSize: (s.fontSize || 16) * scale,
              fontWeight: s.fontWeight || 400,
              fontStyle: s.fontStyle || 'normal',
              textDecoration: decorationOf(s),
              color: gradientText ? 'transparent' : s.color,
              background: gradientText ? text.background : s.background || undefined,
              letterSpacing: s.letterSpacing ? `${s.letterSpacing * scale}px` : 'normal',
              fontVariant: s.smallCaps ? 'small-caps' : 'normal',
              fontFeatureSettings: text.fontFeatureSettings,
              fontVariantLigatures: text.fontVariantLigatures,
              fontVariantNumeric: text.fontVariantNumeric,
              transform: s.shift || s.stretchY ? `${s.shift ? `translateY(${s.shift * (s.fontSize || 16) * scale}px)` : ''}${s.stretchY ? ` scaleY(${s.stretchY})` : ''}`.trim() : undefined,
              // a scaled word (Character Spacing ▸ Scaling) is laid out at its narrower / wider size and stretched back
              // to full height about the line's middle — where the engine centres every chunk, so the baselines meet
              transformOrigin: s.stretchY ? '50% 50%' : undefined,
              textShadow: text.textShadow,
              filter: text.filter,
              WebkitTextStroke: text.WebkitTextStroke,
              hyphens: 'none',
            }}
          >
            {c.text}
          </span>
        );
      }))}
    </div>
  );
};

/**
 * TextContent renders a text box.
 * - editing: contentEditable (uncontrolled while editing; parent syncs via onEdit). The editable is laid out in box
 *   coordinates (unscaled font sizes, indents, inline spans) and zoomed with CSS `zoom`, so what is typed looks exactly
 *   like the display rendering, which scales the same values by the canvas zoom.
 * - textOverride: text coming from a linked previous text box
 * - obstacles: wrap obstacles (see lib/textWrap.js) in content-local, unscaled coordinates
 * - layout: optional precomputed engine layout (CanvasObject computes it for overflow detection)
 */
/** Layout width of the text element in box px: Publisher's horizontal text scaling lays the text out wider / narrower and squeezes it with scaleX (buildTextStyles). */
const textScaleWidth = (w, props) => (props.textScale && props.textScale !== 100 ? (w * 100) / props.textScale : w);

export const TextContent = ({ object, scale = 1, editing = false, onEdit, onExitEdit, onHandOff, editClipAt = null, textOverride, htmlOverride, obstacles = [], layout: layoutProp, contentRef, fitScale = 1, specialChars = false }) => {
  const storedProps = object.properties;
  const rawProps = useMemo(() => storedProps || {}, [storedProps]);
  const { container, text, props } = buildTextStyles(rawProps, scale);
  // Box-space text styles (unscaled): the browser-rendered text — display and editable — is laid out with these at the
  // box's own width and shown through a transform (ScaledBlock), so it wraps exactly as the overflow / linked-flow
  // measurement (textUtils.measureContentHeight) wraps it, at every zoom level.
  const boxText = useMemo(() => buildTextStyles(rawProps, 1).text, [rawProps]);
  const editableRef = useRef(null);
  const boxRef = useRef(null);
  const displayRef = useRef(null);
  const initialHtmlRef = useRef(null);
  const effectiveText = { ...text, fontSize: text.fontSize * fitScale, lineHeight: scaleLineHeight(text.lineHeight, fitScale), letterSpacing: text.letterSpacing };
  // Editing: box-space styles shown at canvas zoom × autofit scale. In local px the text area is contentW / fitScale
  // wide, which is how the display lays out autofitted text (font × fitScale inside contentW).
  const editZoom = scale * fitScale;
  const editText = boxText;
  const fontsVersion = useFontsVersion();

  // Typing past the bottom scrolls the (overflow: hidden) box to keep the caret visible; once editing ends the box must
  // show its top again — otherwise a linked head box appears to show the tail of the story twice (here and in the
  // next linked box).
  useEffect(() => {
    if (!editing && boxRef.current) { boxRef.current.scrollTop = 0; boxRef.current.scrollLeft = 0; }
  }, [editing]);

  // a linked head box edits only its window on the story (htmlOverride), see CanvasObject
  if (editing && initialHtmlRef.current === null) initialHtmlRef.current = htmlOverride !== undefined && htmlOverride !== null ? htmlOverride : getDisplayHtml(props, textOverride);
  if (!editing) initialHtmlRef.current = null;

  const pads = padsOf(props);
  const contentW = object.width - pads.l - pads.r;
  const contentH = object.height - pads.t - pads.b;
  const linkedPrev = !!props.linkPrevId;
  const linkedNext = !!props.linkNextId;
  // A linked box with a next box never scrolls while it is edited: what does not fit is clipped here and shows in the
  // next box at once (CanvasObject's live flow); the caret is handed over there before it could leave the visible
  // part. Should it still end up below the box (a rounding gap between layout and measurement), scrolling is allowed.
  const onBoxScroll = (e) => {
    if (!editing || !linkedNext) return;
    const box = e.currentTarget;
    if (!box.scrollTop) return;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editableRef.current && editableRef.current.contains(sel.anchorNode)) {
      const r = sel.getRangeAt(0).getBoundingClientRect();
      const b = box.getBoundingClientRect();
      if (r.height && r.bottom > b.bottom + 1) return; // the caret really is below the box: let it be seen
    }
    box.scrollTop = 0;
  };
  // The edited linked box shows whole lines only: the words past its room (plain-text offset `editClipAt`, from
  // CanvasObject's live flow — the same split the display uses) are clipped away below the last line that fits, so no
  // half-visible line shows at the bottom and nothing is on screen twice (those words are in the next box already).
  const editOuterRef = useRef(null);
  const applyEditClip = () => {
    const outer = editOuterRef.current;
    if (!outer) return;
    const el = editableRef.current;
    if (!editing || !linkedNext || !el || editClipAt === null || editClipAt === undefined || !Number.isFinite(editClipAt)) { outer.style.clipPath = ''; return; }
    const split = locateSplit(el, editClipAt);
    if (!split) { outer.style.clipPath = ''; return; }
    const range = document.createRange();
    range.setStart(el, 0);
    try { range.setEnd(split.node, split.offset); } catch { outer.style.clipPath = ''; return; }
    let bottom = -Infinity;
    for (const r of range.getClientRects()) if (r.height > 0 && r.width > 0) bottom = Math.max(bottom, r.bottom); // a collapsed rect would sit on the first clipped line
    const ob = outer.getBoundingClientRect();
    const hide = Number.isFinite(bottom) ? ob.bottom - bottom : 0;
    outer.style.clipPath = hide > 0.5 ? `inset(0 0 ${hide}px 0)` : '';
  };
  const applyEditClipRef = useRef(applyEditClip);
  applyEditClipRef.current = applyEditClip;
  useLayoutEffect(() => {
    applyEditClipRef.current();
    const outer = editOuterRef.current;
    return () => { if (outer) outer.style.clipPath = ''; };
  }, [editing, linkedNext, editClipAt, scale, fitScale, object.width, object.height, fontsVersion]);
  // Tab stops: box px → local px of the element that lays the text out
  const editTabs = useMemo(() => tabOptionsOf(props, 1 / (fitScale || 1)), [props, fitScale]);
  const displayTabs = useMemo(() => tabOptionsOf(props, 1), [props]); // the display is laid out in box px (ScaledBlock)
  const editTabsKey = tabOptionsKey(editTabs);

  /** Re-measure the tab spans of the editable (after typing, formatting, a ruler change or a font arriving). */
  const relayoutEditTabs = () => { const el = editableRef.current; if (el && el.querySelector(TAB_SELECTOR)) layoutTabs(el, editTabs); };
  const relayoutRef = useRef(relayoutEditTabs);
  relayoutRef.current = relayoutEditTabs;
  const syncEdit = (el) => { relayoutRef.current(); onEdit && onEdit(cleanEditableHtml(el), el.innerText, caretPlainOffset(el)); };
  const syncEditRef = useRef(syncEdit);
  syncEditRef.current = syncEdit;

  // Entering edit mode: tab characters become measured tab spans (before the caret is placed).
  useLayoutEffect(() => {
    if (!editing || !editableRef.current) return;
    materializeTabs(editableRef.current, { editable: true });
    relayoutRef.current();
  }, [editing]);
  // Stops / default grid / fonts / zoom changed while editing
  useLayoutEffect(() => {
    if (editing) relayoutRef.current();
  }, [editing, editTabsKey, fontsVersion, editZoom, object.width]);

  // While editing, keep the caret usable: approximate the wrap with non-editable float exclusions (box px: the editable
  // is zoomed, its local px are box px / fitScale).
  useEffect(() => {
    if (!editing || !editableRef.current) return undefined;
    const el = editableRef.current;
    el.querySelectorAll('[data-wp-exclusion]').forEach((n) => n.remove());
    if (!obstacles.length) return undefined;
    const lu = 1 / (fitScale || 1);
    const ex = editExclusions(obstacles, contentW);
    ex.forEach((o) => {
      const div = document.createElement('div');
      div.setAttribute('data-wp-exclusion', '1');
      div.setAttribute('contenteditable', 'false');
      div.setAttribute('aria-hidden', 'true');
      // The float spans from the top of the box; shape-outside carves away the part above the
      // obstacle so text can still flow there (a margin-top would push the text away).
      const top = Math.max(0, o.y) * lu;
      Object.assign(div.style, {
        width: o.side === 'full' ? '100%' : `${o.w * lu}px`,
        height: `${top + o.h * lu}px`,
        float: o.side === 'right' ? 'right' : 'left',
        clear: o.side === 'full' ? 'both' : o.side === 'right' ? 'right' : 'left',
        shapeOutside: `inset(${top}px 0 0 0)`,
        pointerEvents: 'none',
        userSelect: 'none',
      });
      el.prepend(div);
    });
    relayoutRef.current();
    return undefined;
  }, [editing, obstacles, contentW, fitScale]);

  useEffect(() => {
    if (!editing || !editableRef.current) return undefined;
    const el = editableRef.current;
    const box = boxRef.current; // the box wrapper lives as long as this object: safe to keep for the cleanup
    editingStore.el = el;
    editingStore.objectId = object.id;
    editingStore.sync = () => { relayoutRef.current(); onEdit && onEdit(cleanEditableHtml(el), el.innerText, caretPlainOffset(el)); };
    // linked story: words that fit back into this box while it is edited are appended to the editable (CanvasObject's
    // live flow, storyEdit.appendStoryFragment) — the caret stays where it is
    editingStore.appendHtml = (fragmentHtml, midBlock, joiner, opts) => {
      const ok = appendStoryFragment(el, fragmentHtml, midBlock, joiner, opts);
      if (ok) { relayoutRef.current(); saveSelection(); }
      return ok;
    };
    // A caret that is already inside the editable *before* it is focused stays (this effect re-runs on a mount in
    // React's StrictMode — a hand-off below must not be undone by the re-run). Read before focus(): focusing an
    // editable that holds no selection puts the browser's own caret at its start.
    const sel0 = window.getSelection();
    const kept = sel0 && sel0.rangeCount && sel0.isCollapsed && el.contains(sel0.anchorNode) ? sel0.getRangeAt(0).cloneRange() : null;
    el.focus();
    const pending = editingStore.pendingCaret;
    const point = editingStore.pendingPoint;
    editingStore.pendingPoint = null;
    if (pending && pending.objectId === object.id) {
      // editing handed over from another box of the story: the caret continues where the text went (storyEdit.placeCaretAtPlainOffset)
      editingStore.pendingCaret = null;
      placeCaretAtPlainOffset(el, pending.offset);
    } else if (kept) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(kept);
    } else if (!(point && point.objectId === object.id && placeCaretAtPoint(el, point.x, point.y))) {
      // started by a double-click: the caret goes where the user clicked (Publisher); otherwise to the end of the text
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
    saveSelection();
    // Caret moved (click / arrow keys): remember it; an unused typing-style span the caret left behind goes.
    const onSel = () => { saveSelection(); if (pruneTypingSpans(el)) syncEditRef.current(el); };
    document.addEventListener('selectionchange', onSel);
    // Focus leaves for a dialog / the ribbon / a pane: the browser stops drawing the highlight, so the remembered range
    // is painted instead (Custom Highlight API) until focus comes back — the user keeps seeing what a dialog will change.
    const onFocusOut = () => { window.setTimeout(() => { if (editingStore.el === el && document.activeElement !== el) showRememberedSelection(); }, 0); };
    const onFocusIn = () => hideRememberedSelection();
    el.addEventListener('focusout', onFocusOut);
    el.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('selectionchange', onSel);
      el.removeEventListener('focusout', onFocusOut);
      el.removeEventListener('focusin', onFocusIn);
      hideRememberedSelection();
      if (editingStore.el === el) {
        editingStore.el = null;
        editingStore.objectId = null;
        editingStore.sync = null;
        editingStore.appendHtml = null;
        editingStore.savedRange = null;
        // the highlight must not linger on the display rendering that takes the editable's place
        try { const s = window.getSelection(); if (s && s.rangeCount && !s.isCollapsed && (el.contains(s.anchorNode) || (box && box.contains(s.anchorNode)))) s.removeAllRanges(); } catch { /* ignore */ }
      }
    };
  }, [editing, object.id, onEdit]);

  // htmlOverride: a linked head box shows only the part of its (rich) story that does not flow on to the next box
  const html = useMemo(() => (htmlOverride !== undefined && htmlOverride !== null ? htmlOverride : getDisplayHtml(props, textOverride)), [props, textOverride, htmlOverride]);

  // Engine layout: used whenever obstacles affect this box, or the box uses features only the engine renders
  // (Align to Baselines, "Rotate all text 90°") — and we are not editing.
  const vertical = isVerticalText(props);
  const engineOn = !editing && (obstacles.length > 0 || needsEngine(props)) && isEngineEligible(props);
  // Align to Baselines depends on the publication grid (module store, synced by the EditorProvider on every render)
  const gridKey = props.alignToBaselines ? `${baselineGrid.spacing}:${baselineGrid.offset}` : '';
  const layout = useMemo(() => {
    if (!engineOn) return null;
    if (layoutProp) return layoutProp;
    return layoutTextBox(props, html, contentW, contentH, obstacles, { fontScale: fitScale, originY: (object.y || 0) + pads.t });
    // gridKey is an extra trigger on purpose: layoutTextBox reads the baseline grid from the module store, not from a prop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineOn, layoutProp, props, html, contentW, contentH, obstacles, fitScale, object.y, pads.t, gridKey]);

  // wp-text-fill: gradient / picture text fill — the class pins background-clip: text (see index.css); an inline
  // `background` shorthand update alone would otherwise reset the clip and paint the whole block.
  const classes = `wp-text ${props.dropCap ? 'wp-dropcap' : ''} ${props.spaceBefore || props.spaceAfter ? 'wp-para' : ''} ${text.background ? 'wp-text-fill' : ''}`;
  const warped = !editing && hasWordArtTransform(props);
  // Tab key inserts a tab stop (an atomic tab span) at the live caret while editing
  const onEditKeyDown = (e) => {
    if (e.key === 'Escape') { e.currentTarget.blur(); onExitEdit && onExitEdit(); }
    else if (e.key === 'Backspace') {
      // at the very start of a continuation box the caret goes back to the end of the box before (Publisher)
      const sel = window.getSelection();
      if (linkedPrev && onHandOff && sel && sel.isCollapsed && caretPlainOffset(e.currentTarget) === 0 && onHandOff('prev')) { e.preventDefault(); e.stopPropagation(); return; }
      // right after an unused typing-style placeholder: drop it so the key deletes real text (not the invisible space)
      dropTypingSpanBeforeBackspace(e.currentTarget);
    }
    else if ((e.key === 'Home' || e.key === 'End') && (e.ctrlKey || e.metaKey) && !e.shiftKey && onHandOff) {
      // Ctrl+Home / Ctrl+End: the start / end of the whole story, which may be in another box
      if ((e.key === 'Home' && linkedPrev && onHandOff('first')) || (e.key === 'End' && linkedNext && onHandOff('last'))) { e.preventDefault(); e.stopPropagation(); return; }
    }
    else if (e.key === 'Tab') {
      e.preventDefault();
      const el = e.currentTarget;
      const sel = window.getSelection();
      if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const span = createTabSpan(true);
        range.insertNode(span);
        range.setStartAfter(span);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else document.execCommand('insertText', false, '\t');
      saveSelection();
      syncEdit(el);
    }
    e.stopPropagation();
  };
  const onEditInput = (e) => {
    const el = e.currentTarget;
    // text typed beside the first / last paragraph (caret at the editable root after Ctrl+Home / Ctrl+End) joins it
    if (absorbRootText(el)) saveSelection();
    // typed into a typing-style span: its zero-width placeholder goes, the text keeps the span's style
    if (settleTypingSpans(el)) saveSelection();
    // a space typed inside a scaled word (Character Spacing ▸ Scaling): one scaled span per word again, caret kept
    normalizeScaleSpans(el);
    // a pasted / IME-inserted tab character becomes a tab span, caret kept
    if (el.textContent.includes('\t') && materializeTabs(el, { editable: true, keepCaret: true })) saveSelection();
    syncEdit(el);
  };

  const box = (
    <div ref={boxRef} style={editing && linkedNext && container.justifyContent && container.justifyContent !== 'flex-start' ? { ...container, justifyContent: `safe ${container.justifyContent}` } : container} className="wp-textbox" onScroll={onBoxScroll} data-testid="textbox-content">
      {editing ? (
        <ScaledBlock
          key="edit"
          scale={editZoom}
          width={textScaleWidth(contentW / (fitScale || 1), props)}
          innerRef={(el) => { editableRef.current = el; if (contentRef) contentRef.current = el; }}
          outerRef={editOuterRef}
          className={`${classes} ${specialChars ? 'wp-sc-editing' : ''}`}
          style={{ ...editText, width: undefined, cursor: 'text', userSelect: 'text', tabSize: `${props.defaultTabStop || DEFAULT_TAB_STOP}px` }}
          contentEditable
          suppressContentEditableWarning
          spellCheck
          lang="en"
          dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }}
          onInput={onEditInput}
          onPaste={(e) => {
            e.preventDefault();
            const t = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, t);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={onEditKeyDown}
          data-testid="textbox-editable"
          data-zoom={editZoom}
        />
      ) : warped ? (
        <div ref={contentRef} className="wp-text" style={{ flex: 1, minHeight: 0, width: '100%', opacity: 1 }} data-testid="wordart-content">
          <WordArtSvg object={object} scale={scale} text={textOverride ?? props.text ?? ''} contentW={contentW} contentH={contentH} />
        </div>
      ) : layout && vertical ? (
        // "Rotate all text 90°": the engine laid the text out in a frame with swapped axes; rotate it clockwise into the box.
        <div style={{ position: 'relative', flex: 1, width: '100%', minHeight: 0 }} data-testid="textbox-vertical">
          <div style={{ position: 'absolute', left: 0, top: 0, width: contentH * scale, height: contentW * scale, transform: `translate(${contentW * scale}px, 0) rotate(90deg)`, transformOrigin: '0 0' }}>
            <LayoutLines layout={layout} scale={scale} text={{ ...effectiveText, writingMode: undefined }} classes={classes} />
          </div>
        </div>
      ) : layout ? (
        <LayoutLines layout={layout} scale={scale} text={effectiveText} classes={classes} />
      ) : (
        // box-space layout (font × fitScale inside contentW) shown at canvas zoom — the wrapping the measurement saw
        <ScaledBlock key="display" scale={scale} width={textScaleWidth(contentW, props)} measureKey={`${html}|${fitScale}|${fontsVersion}`} innerRef={(el) => { displayRef.current = el; if (contentRef) contentRef.current = el; }} className={classes} style={{ ...boxText, width: undefined, fontSize: boxText.fontSize * fitScale, tabSize: `${props.defaultTabStop || DEFAULT_TAB_STOP}px` }} lang="en" data-testid="textbox-display">
          <TabbedHtml html={scaleInlineHtml(specialChars ? markSpecialChars(html) : html, fitScale)} tabs={displayTabs} rootRef={displayRef} />
        </ScaledBlock>
      )}
    </div>
  );
  // Gradient outline: the (transparent) CSS border keeps the layout; the gradient is painted in the border zone by an
  // overlay outside the clipping box (overflow: hidden would clip it inside).
  if (!(props.stroke?.width > 0 && strokeHasGradient(props.stroke))) return box;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} data-gradient-outline="1">
      {box}
      <OutlineSvg id={`wp-${object.id}-ol`} stroke={props.stroke} width={object.width * scale} height={object.height * scale} strokeWidth={cssBorderPx(props.stroke.width, scale)} radius={(props.cornerRadius || 0) * scale} opacity={(props.opacity ?? 100) / 100} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Images                                                              */
/* ------------------------------------------------------------------ */

export const ImageContent = ({ object, scale = 1 }) => {
  const p = object.properties || {};
  const filters = [];
  if (p.brightness !== undefined && p.brightness !== 100) filters.push(`brightness(${p.brightness}%)`);
  if (p.contrast !== undefined && p.contrast !== 100) filters.push(`contrast(${p.contrast}%)`);
  if (p.saturation !== undefined && p.saturation !== 100) filters.push(`saturate(${p.saturation}%)`);
  if (p.grayscale) filters.push('grayscale(1)');
  if (p.sepia) filters.push('sepia(1)');
  if (p.blur) filters.push(`blur(${p.blur}px)`);
  const transform = `${p.flipX ? 'scaleX(-1)' : ''} ${p.flipY ? 'scaleY(-1)' : ''}`.trim();
  const stroke = p.stroke || {};
  // In-place crop: the picture's own rectangle relative to the frame (see lib/pictureCrop.js); the frame clips it.
  const crop = hasCrop(p) ? p.crop : null;
  // Crop to Shape: the frame is clipped to a gallery shape; the border then follows the outline (drawn as a shape).
  const shape = isCropShape(p.cropShape) ? p.cropShape : null;
  const clipId = `wp-cs-${object.id}`;
  const clip = shape ? cropShapeClip(shape, object.width * scale, object.height * scale, (p.borderRadius || 0) * scale, clipId) : null;
  const bw = stroke.width && !shape ? cssBorderPx(stroke.width, scale) : 0;
  const gradientBorder = bw > 0 && strokeHasGradient(stroke);
  const frame = (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative', borderRadius: shape ? 0 : (p.borderRadius || 0) * scale, border: bw ? `${bw}px ${gradientBorder ? 'solid transparent' : `${cssDash(stroke.dash)} ${stroke.color || '#000'}`}` : 'none', boxSizing: 'border-box', background: p.placeholder ? (p.placeholderTint || '#e8ebf0') : undefined, clipPath: clip || undefined, WebkitClipPath: clip || undefined }} data-crop-shape={shape || undefined}>
      {p.src && crop ? (
        <img
          src={p.src}
          alt={p.altText || ''}
          crossOrigin={p.src.startsWith('data:') ? undefined : 'anonymous'}
          style={{ position: 'absolute', left: crop.x * scale - bw, top: crop.y * scale - bw, width: crop.w * scale, height: crop.h * scale, maxWidth: 'none', opacity: (p.opacity ?? 100) / 100, filter: filters.join(' ') || undefined, transform: transform || undefined, display: 'block' }}
          draggable={false}
          data-cropped="1"
        />
      ) : p.src ? (
        <img
          src={p.src}
          alt={p.altText || ''}
          crossOrigin={p.src.startsWith('data:') ? undefined : 'anonymous'}
          style={{ width: '100%', height: '100%', objectFit: p.objectFit || 'cover', objectPosition: p.objectPosition || 'center', opacity: (p.opacity ?? 100) / 100, filter: filters.join(' ') || undefined, transform: transform || undefined, display: 'block' }}
          draggable={false}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 * scale, color: '#7c8494', fontSize: 11 * scale, fontFamily: 'Inter, sans-serif', border: `${Math.max(1, scale)}px dashed #b3bac6`, boxSizing: 'border-box', overflow: 'hidden' }} data-testid="picture-placeholder">
          <svg width={Math.min(object.width, object.height) * scale * 0.28} height={Math.min(object.width, object.height) * scale * 0.28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" /></svg>
          {Math.min(object.width, object.height) * scale > 60 && <span>{p.placeholderLabel || 'Picture'}</span>}
        </div>
      )}
    </div>
  );
  if (!shape) {
    if (!gradientBorder) return frame;
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }} data-gradient-outline="1">
        {frame}
        <OutlineSvg id={`wp-${object.id}-ol`} stroke={stroke} width={object.width * scale} height={object.height * scale} strokeWidth={bw} radius={(p.borderRadius || 0) * scale} />
      </div>
    );
  }
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {cropShapeNeedsDefs(shape) && (
        <svg width="0" height="0" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" focusable="false">
          <clipPath id={clipId} clipPathUnits="objectBoundingBox">
            <path d={cropShapePath(shape)} transform="scale(0.01)" clipRule={cropShapeEvenOdd(shape) ? 'evenodd' : undefined} />
          </clipPath>
        </svg>
      )}
      {frame}
      {stroke.width > 0 && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} data-testid="crop-shape-outline">
          <ShapeSvg shapeType={shape} id={`${object.id}-cso`} width={object.width} height={object.height} scale={scale} props={{ fill: { type: 'none' }, stroke, opacity: 100 }} />
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Tables                                                              */
/* ------------------------------------------------------------------ */

export const TableContent = ({ object, scale = 1, editing, onCellChange }) => {
  const p = object.properties || {};
  const rows = p.rows || 2;
  const cols = p.cols || 2;
  const cells = p.cells || [];
  // Gradient borders (borderGradient): the cells keep transparent borders of the same weight so nothing moves, and
  // TableBordersSvg paints the frame + cell rules with one gradient across the whole grid, at the measured boundaries.
  const borderStroke = tableBorderStroke(p);
  const gradientBorders = (p.borderWidth ?? 1) > 0 && strokeHasGradient(borderStroke);
  const bw = gradientBorders ? cssBorderPx(p.borderWidth ?? 1, scale) : (p.borderWidth ?? 1) * scale;
  const border = `${bw}px solid ${gradientBorders ? 'transparent' : p.borderColor || '#000'}`;
  const tableRef = useRef(null);
  const cellsRef = useRef(cells);
  cellsRef.current = cells;
  const [grid, setGrid] = useState(null); // { w, h, xs, ys } in px, from the rendered table
  useLayoutEffect(() => {
    if (!gradientBorders || !tableRef.current) return undefined;
    const table = tableRef.current;
    const measure = () => {
      const tr = table.getBoundingClientRect();
      const firstRow = table.querySelector('tr');
      const tds = firstRow ? [...firstRow.querySelectorAll('td')] : [];
      const trs = [...table.querySelectorAll('tbody > tr')];
      const xs = tds.slice(0, -1).map((td) => td.getBoundingClientRect().right - tr.left);
      const ys = trs.slice(0, -1).map((row) => row.getBoundingClientRect().bottom - tr.top);
      const next = { w: tr.width, h: tr.height, xs, ys };
      setGrid((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(table);
    table.querySelectorAll('tbody > tr').forEach((row) => ro.observe(row));
    return () => ro.disconnect();
  }, [gradientBorders, rows, cols, p.cells, scale, bw, p.cellPadding, p.fontSize, p.fontFamily, object.width, object.height, editing]);
  // Cells commit on blur while editing; when editing ends through a click elsewhere the blur can arrive after the
  // handler is gone, so sweep every cell once on exit and commit whatever still differs from the model.
  useEffect(() => {
    if (editing || !tableRef.current || !onCellChange) return;
    const changes = [];
    tableRef.current.querySelectorAll('td[data-row]').forEach((td) => {
      const r = Number(td.dataset.row), c = Number(td.dataset.col);
      const text = td.innerText.replace(/\n$/, '');
      if ((cellsRef.current[r]?.[c]?.text || '') !== text) changes.push([r, c, text]);
    });
    if (changes.length) onCellChange(changes);
  }, [editing, onCellChange]);
  const table = (
    <table ref={tableRef} style={{ width: '100%', height: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontFamily: `'${p.fontFamily || 'Inter'}', sans-serif`, fontSize: (p.fontSize || 14) * scale, color: p.color || '#000', background: fillToCss(p.fill), opacity: (p.opacity ?? 100) / 100 }} data-testid="table-content">
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r} style={{ height: `${100 / rows}%` }}>
            {Array.from({ length: cols }).map((__, c) => {
              const cell = cells[r]?.[c] || {};
              const isHeader = p.headerRow && r === 0;
              const bandFill = p.banded && r % 2 === 1 ? 'rgba(0,0,0,0.05)' : undefined;
              return (
                <td
                  key={c}
                  style={{ border, padding: (p.cellPadding ?? 4) * scale, verticalAlign: cell.valign || 'top', fontWeight: cell.bold != null ? (cell.bold ? 700 : 400) : (isHeader ? 700 : 400), background: cell.fill || (isHeader ? p.headerFill || 'rgba(124,58,237,0.12)' : bandFill), textAlign: cell.align || 'left', color: cell.color || undefined, fontSize: cell.fontSize ? cell.fontSize * scale : undefined, fontStyle: cell.italic ? 'italic' : undefined, overflow: 'hidden', wordBreak: 'break-word' }}
                  data-row={r}
                  data-col={c}
                  contentEditable={editing}
                  suppressContentEditableWarning
                  onBlur={editing ? (e) => onCellChange && onCellChange(r, c, e.currentTarget.innerText) : undefined}
                  onMouseDown={editing ? (e) => e.stopPropagation() : undefined}
                  onKeyDown={editing ? (e) => e.stopPropagation() : undefined}
                >
                  {cell.text || ''}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
  if (!gradientBorders) return table;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} data-gradient-outline="1">
      {table}
      {grid && <TableBordersSvg id={`wp-${object.id}-tb`} stroke={borderStroke} width={grid.w} height={grid.h} strokeWidth={bw} xs={grid.xs} ys={grid.ys} opacity={(p.opacity ?? 100) / 100} />}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Effects frame + dispatcher                                          */
/* ------------------------------------------------------------------ */

/**
 * Wraps an object's content with Publisher-style effects: shadow (outer / inner / perspective),
 * reflection, glow, soft edges, bevel and 3-D rotation. Renders nothing extra when no effects are set.
 * `clone` is a static, ref-free render of the same content used for reflection / cast-shadow copies.
 */
export const EffectsFrame = ({ object, scale = 1, editing = false, clone, children }) => {
  const p = object.properties;
  const reactId = useId();
  const uid = useMemo(() => `fx-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`, [reactId]);
  const fx = useMemo(() => buildFrameEffects(p || {}, scale, uid, { width: object.width, height: object.height }), [p, scale, uid, object.width, object.height]);
  const showClones = !editing && clone;
  const refl = fx.reflection;
  const mask = refl ? `linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) ${Math.round(refl.visible * 100)}%)` : undefined;
  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative', filter: fx.filter, transform: fx.transform, transformStyle: fx.transform ? 'preserve-3d' : undefined, backfaceVisibility: fx.transform ? 'visible' : undefined }}
      data-testid="effects-frame"
      data-effects={FRAME_KEYS_PRESENT(p || {})}
    >
      {fx.defs.length > 0 && <svg width="0" height="0" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" focusable="false"><defs>{fx.defs}</defs></svg>}
      {showClones && fx.cast && (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, transform: fx.cast.transform, transformOrigin: fx.cast.origin, filter: `brightness(0) blur(${fx.cast.blur / 2}px)`, opacity: fx.cast.opacity, pointerEvents: 'none' }} data-testid="effects-cast-shadow">{clone}</div>
      )}
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>{children}</div>
      {showClones && refl && (
        <div aria-hidden="true" style={{ position: 'absolute', left: 0, top: `calc(100% + ${refl.offset}px)`, width: '100%', height: '100%', transform: 'scaleY(-1)', opacity: refl.opacity, pointerEvents: 'none', WebkitMaskImage: mask, maskImage: mask }} data-testid="effects-reflection">{clone}</div>
      )}
    </div>
  );
};
const FRAME_KEYS_PRESENT = (p) => ['shadow', 'reflection', 'glow', 'softEdges', 'bevel', 'rotation3d'].filter((k) => p[k]).join(' ') || undefined;

const renderInner = ({ object, scale = 1, editing = false, onEdit, onExitEdit, onHandOff, editClipAt, textOverride, htmlOverride, obstacles, layout, contentRef, fitScale, onCellChange, specialChars }) => {
  switch (object.type) {
    case 'shape':
      return (
        <>
          <ShapeSvg shapeType={object.shapeType} props={object.properties} id={object.id} width={object.width} height={object.height} scale={scale} />
          {(editing || hasShapeText(object)) && (
            // Publisher "Edit Text": the shape's own text, laid out in its inset text rectangle
            <div style={{ position: 'absolute', inset: 0 }} data-testid="shape-text">
              <TextContent object={shapeTextObject(object)} scale={scale} editing={editing} onEdit={onEdit} onExitEdit={onExitEdit} contentRef={contentRef} fitScale={fitScale || 1} />
            </div>
          )}
        </>
      );
    case 'textbox':
    case 'text':
      return <TextContent object={object} scale={scale} editing={editing} onEdit={onEdit} onExitEdit={onExitEdit} onHandOff={onHandOff} editClipAt={editClipAt} textOverride={textOverride} htmlOverride={htmlOverride} obstacles={obstacles} layout={layout} contentRef={contentRef} fitScale={fitScale} specialChars={specialChars} />;
    case 'image':
      return <ImageContent object={object} scale={scale} />;
    case 'table':
      return <TableContent object={object} scale={scale} editing={editing} onCellChange={onCellChange} />;
    case 'group':
      return <GroupContent object={object} scale={scale} />;
    default:
      return null;
  }
};

export const ObjectRenderer = (props) => {
  const { object, scale = 1, editing = false } = props;
  if (!hasFrameEffects(object.properties)) return renderInner(props);
  const needsClone = !!(object.properties.reflection || object.properties.shadow?.cast);
  const clone = needsClone ? renderInner({ object, scale, textOverride: props.textOverride, htmlOverride: props.htmlOverride, obstacles: props.obstacles, layout: props.layout, fitScale: props.fitScale }) : null;
  return <EffectsFrame object={object} scale={scale} editing={editing} clone={clone}>{renderInner(props)}</EffectsFrame>;
};

export const GroupContent = ({ object, scale = 1 }) => {
  const children = object.children || [];
  const bw = Math.max(1, children.reduce((m, c) => Math.max(m, c.x + c.width), 0));
  const bh = Math.max(1, children.reduce((m, c) => Math.max(m, c.y + c.height), 0));
  const sx = object.width / bw;
  const sy = object.height / bh;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {children.map((child) => (
        <div key={child.id} style={{ position: 'absolute', left: child.x * sx * scale, top: child.y * sy * scale, width: child.width * sx * scale, height: child.height * sy * scale, transform: `rotate(${child.rotation || 0}deg)`, opacity: child.visible === false ? 0 : 1 }}>
          <ObjectRenderer object={{ ...child, width: child.width * sx, height: child.height * sy }} scale={scale} />
        </div>
      ))}
    </div>
  );
};

/** Static (non-interactive) page for export, thumbnails and save-as-picture. */
export const StaticPage = ({ page, master, scale = 1, textFlow = {}, objectsOverride, showBackground = true, className, style }) => {
  const masterObjects = (master?.objects || []).filter((o) => o.visible !== false);
  const objects = (objectsOverride || page.objects || []).filter((o) => o.visible !== false).sort((a, b) => a.layer - b.layer);
  const bg = page.background || { type: 'solid', color: '#ffffff' };
  const isText = (o) => o.type === 'textbox' || o.type === 'text';
  return (
    <div className={className} style={{ position: 'relative', width: page.width * scale, height: page.height * scale, background: showBackground ? fillToCss(bg, '#ffffff') : 'transparent', overflow: 'hidden', ...style }} data-testid="static-page">
      {masterObjects.map((obj) => (
        <div key={`m-${obj.id}`} style={{ position: 'absolute', left: obj.x * scale, top: obj.y * scale, width: obj.width * scale, height: obj.height * scale, transform: `rotate(${obj.rotation || 0}deg)`, zIndex: 0 }}>
          <ObjectRenderer object={obj} scale={scale} obstacles={isText(obj) ? computeWrapObstacles(obj, masterObjects) : undefined} />
        </div>
      ))}
      {objects.map((obj) => {
        const linked = isText(obj) && (obj.properties?.linkPrevId || obj.properties?.linkNextId) ? linkedDisplay(obj, textFlow) : null;
        return (
          <div key={obj.id} style={{ position: 'absolute', left: obj.x * scale, top: obj.y * scale, width: obj.width * scale, height: obj.height * scale, transform: `rotate(${obj.rotation || 0}deg)`, zIndex: (obj.layer || 0) + 1 }}>
            <ObjectRenderer object={obj} scale={scale} textOverride={linked?.textOverride} htmlOverride={linked?.htmlOverride} obstacles={isText(obj) ? computeWrapObstacles(obj, objects) : undefined} />
          </div>
        );
      })}
    </div>
  );
};
